import { ColumnHelper } from '@tanstack/react-table'
import { Badge } from '@wartek-id/badge'
import { Text } from '@wartek-id/text'

import { BasicDataRow, NestedColumnType, NestedDataRow } from '@/apis/types'

import { formatNumber } from './string'

export const CellRenderer = ({ getValue }) => {
  const value = getValue()

  if (typeof value === 'number') {
    return (
      <div data-type="number" className="text-right">
        <Text>{formatNumber(value)}</Text>
      </div>
    )
  }

  if (typeof value === 'boolean') {
    return (
      <div data-type="boolean" className="text-center">
        <Badge variant={value ? 'success' : 'critical'}>
          {value ? 'Ya' : 'Tidak'}
        </Badge>
      </div>
    )
  }

  if (Array.isArray(value)) {
    return (
      <div data-type="array" className="text-left">
        {(value || [])?.map((item, index) => (
          <Text key={item} data-type={`item-${index}`}>
            {item}
          </Text>
        ))}
      </div>
    )
  }

  return (
    <div data-type="default">
      <Text>{value}</Text>
    </div>
  )
}

export function generateNestedColumns<TData>(
  columnHelper: ColumnHelper<TData>,
  col: NestedColumnType,
  CustomCellRenderer?: ({ row, col }) => JSX.Element,
  enableSortColumns?: Record<string, number>
) {
  return columnHelper.group({
    header: col.displayName,
    columns: (col?.childColumns || [])?.map((childColumn) => {
      if (childColumn?.childColumns && childColumn?.childColumns?.length > 0) {
        return generateNestedColumns(
          columnHelper,
          childColumn,
          CustomCellRenderer,
          enableSortColumns
        )
      }

      // @ts-ignore
      return columnHelper.accessor(childColumn.id, {
        header: childColumn.displayName,
        enableSorting:
          enableSortColumns && enableSortColumns[childColumn.id] === 1,
        cell: (row) =>
          CustomCellRenderer ? (
            <CustomCellRenderer row={row} col={childColumn} />
          ) : (
            <CellRenderer getValue={row.getValue} />
          ),
      })
    }),
  })
}

export function generateColumns<TData>(
  columnHelper: ColumnHelper<TData>,
  columns: NestedColumnType[],
  CustomCellRenderer?: ({ row, col }) => JSX.Element,
  enableSortColumns?: Record<string, number>
) {
  return columns?.map((col: NestedColumnType) => {
    if (col?.childColumns && col?.childColumns?.length > 0) {
      return generateNestedColumns(
        columnHelper,
        col,
        CustomCellRenderer,
        enableSortColumns
      )
    }

    // @ts-ignore
    return columnHelper.accessor(col?.id, {
      header: col.displayName,
      enableSorting: enableSortColumns && enableSortColumns[col.id] === 1,
      cell: (row) =>
        CustomCellRenderer ? (
          <CustomCellRenderer row={row} col={col} />
        ) : (
          <CellRenderer getValue={row.getValue} />
        ),
    })
  })
}

export function flattenChildrenNestedRow(
  firstColumn: string,
  item: NestedDataRow,
  startIndex: number,
  numberIndex: number
): BasicDataRow[] {
  const results: BasicDataRow[] = []
  if (typeof item.Values !== 'undefined') {
    results.push({
      number: startIndex === 1 ? numberIndex : 0,
      id: item.ID,
      tab: startIndex,
      [firstColumn]: item.DisplayName,
      span: 0,
      ...item.Values,
    })

    if (startIndex === 1) {
      numberIndex += 1
    }
  }

  if (
    typeof item.ChildRows !== 'undefined' &&
    item.ChildRows !== null &&
    item.ChildRows.length > 0
  ) {
    const allData = item.ChildRows.reduce((accumulator, currentValue) => {
      const childs = flattenChildrenNestedRow(
        firstColumn,
        currentValue,
        startIndex + 1,
        numberIndex
      )

      // @ts-ignore
      return accumulator.concat(childs)
    }, [])

    const isHaveValues = typeof item.Values !== 'undefined'
    const parentRow = isHaveValues
      ? []
      : [
          {
            id: item.ID,
            tab: startIndex,
            [firstColumn]: item.DisplayName,
            span: 1,
            number: startIndex === 1 ? numberIndex : 0,
          },
        ]
    return [...results, ...parentRow, ...allData] as BasicDataRow[]
  } else {
  }

  return results
}

export function flattenNestedRows(
  firstColumn: string,
  rows: NestedDataRow[],
  startIndex: number
): BasicDataRow[] {
  const allData = rows.reduce((accumulator, currentValue, currentIndex) => {
    const childs = flattenChildrenNestedRow(
      firstColumn,
      currentValue,
      startIndex,
      currentIndex + 1
    )

    // @ts-ignore
    return accumulator.concat(childs)
  }, [])

  return allData
}

export function getHeaderClass(colSpan = 1, firstRowType) {
  const baseClass = '!px-2 !py-4'

  if (colSpan > 1) {
    return `${baseClass} text-center`
  }

  if (typeof firstRowType === 'number') {
    return `${baseClass} text-right`
  }

  return `${baseClass} text-left`
}
